/**
 * AI insights client — the two one-shot Mistral endpoints on the AiBridge:
 *   POST /ai/parse-query  (natural language → FilterSpec for the drill views)
 *   POST /ai/explain      (server-gathered facts → Lithuanian narration)
 *
 * Same transport contract as aiChat.ts: Bearer from the in-memory token when
 * present, otherwise the host-scoped httpOnly cookie; X-Workspace-Id always.
 * Base URL: VITE_AI_BRIDGE_URL (dev, e.g. http://localhost:5291) else the
 * same-origin /ai prefix (pilot — nginx routes /ai/ to the bridge).
 */
import { getToken } from './client';

// Dev talks to the bridge port directly; the production build uses the
// same-origin /ai prefix (nginx routes /ai/ to the bridge container process).
const AI_BRIDGE_URL: string = import.meta.env.VITE_AI_BRIDGE_URL
  ?? (import.meta.env.DEV ? `http://${window.location.hostname}:5291` : '/ai');
const WORKSPACE_ID_KEY = 'a5core_workspace_id';

function aiHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const workspaceId = localStorage.getItem(WORKSPACE_ID_KEY);
  if (workspaceId) headers['X-Workspace-Id'] = workspaceId;
  return headers;
}

async function post<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(`${AI_BRIDGE_URL}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...aiHeaders(),
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`AI Bridge error ${response.status}: ${text}`);
  }
  return response.json() as Promise<T>;
}

export type FilterOp = '>=' | '<=' | '==' | 'contains';

export interface FilterCondition {
  key: string;
  op: FilterOp;
  value: string | number;
}

export interface FilterSpec {
  view: 'customers' | 'invoices' | 'payments' | 'findings';
  filters: FilterCondition[];
  sort?: { key: string; dir: 'asc' | 'desc' };
  search?: string;
}

export interface ParseQueryResult {
  spec: FilterSpec | null;
  /** True when the model output failed validation and we fell back to plain search. */
  fallback: boolean;
}

export interface ExplainResult {
  text: string;
  factsCount: number;
}

export const insightsApi = {
  /**
   * Parse a natural-language query into a drill filter. Never throws for
   * model-level failures — returns { spec: null, fallback: true } so the UI
   * can degrade to ordinary text search.
   */
  parseQuery: async (text: string, context?: { view?: string }): Promise<ParseQueryResult> => {
    try {
      const result = await post<{ spec: FilterSpec | null; fallback?: boolean }>('/parse-query', {
        text,
        context: context ?? {},
      });
      return { spec: result?.spec ?? null, fallback: !!result?.fallback };
    } catch {
      return { spec: null, fallback: true };
    }
  },

  /** Narrate a drill view from server-gathered facts (numbers are whitelisted bridge-side). */
  explain: (view: string, entityId?: number): Promise<ExplainResult> =>
    post<ExplainResult>('/explain', { view, entityId }),
};
